import * as React from 'react';
import { Alert, Button, Modal, Select, Textarea } from '@ixicare/ui';
import { useMutation } from '@tanstack/react-query';
import classnames from 'classnames';
import {
  Beacon,
  DecommissionBody,
  DecommissionReason,
  decommissionReasons,
  Device,
  DeviceType,
  Gateway,
  MAX_DECOMMISSION_NOTE_LENGTH,
} from '@ixicare/resources';
import { t } from '~i18n';
import { Spacer } from '~components/Utils/Spacer';
import { humaniseErrors } from '~utils/error';
import { DBGWithType } from '~modules/devices/components/DeviceHealthTable/types';
import { decommissionReasonLabel } from '~modules/devices/utils/deviceState';
import styles from '~/components/ActionBar/ActionBar.module.css';

const MAX_CHIPS = 7;

interface DecommissionDeviceModalProps {
  devices: DBGWithType[];
  onClose: () => void;
  onRefresh: () => void;
}

const decommissionOne = (device: DBGWithType, body: DecommissionBody) => {
  if (device.type === DeviceType.GATEWAY) return Gateway.decommissionGateway(Number(device.id), body);
  if (device.type === DeviceType.BEACON) return Beacon.decommissionBeacon(Number(device.id), body);
  return Device.decommissionDevice(Number(device.id), body);
};

export function DecommissionDeviceModal({ devices, onClose, onRefresh }: DecommissionDeviceModalProps) {
  const [reason, setReason] = React.useState<DecommissionReason | null>(null);
  const [note, setNote] = React.useState('');

  const shownDevices = devices.slice(0, MAX_CHIPS);
  const overflow = devices.length - shownDevices.length;

  const reasonOptions = React.useMemo(
    () => decommissionReasons.map((value) => ({ value, label: decommissionReasonLabel(value) })),
    []
  );

  // The backend requires a note for OTHER — "other" on its own says nothing that
  // could later be argued from — so ask for it here rather than round-trip a 400.
  const noteMissing = reason === DecommissionReason.OTHER && !note.trim();
  const canSubmit = !!reason && !noteMissing;

  const mutation = useMutation({
    mutationFn: async () => {
      const body: DecommissionBody = { reason: reason!, note: note.trim() || undefined };
      const results = await Promise.allSettled(devices.map((device) => decommissionOne(device, body)));
      const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
      // Nothing went through: surface the error and keep the modal open rather than
      // closing on a silent no-op.
      if (failures.length === results.length) throw failures[0].reason;
      return { failures };
    },
    onSuccess: () => {
      onRefresh();
      onClose();
    },
  });

  return (
    <Modal onClose={onClose} style={{ maxWidth: 450 }}>
      <h3 className="text-center mb-10">{t('device.decommission.title')}</h3>
      <ul className={styles.chips}>
        {shownDevices.map((device) => (
          <li className={classnames('text-center', 'mb-10', styles.chip)} key={device.macAddress}>
            {device.macAddress} - {device.description}
          </li>
        ))}
        {overflow > 0 && <li className={classnames('text-center', styles.chip)}>+{overflow}</li>}
      </ul>
      {mutation.error && <Alert type="error">{humaniseErrors(mutation.error as Error).title}</Alert>}
      <Alert>{t('device.decommission.explanation')}</Alert>
      <Spacer size={16} />
      <Select
        label={t('device.decommission.reason')}
        placeholder={t('device.decommission.reasonPlaceholder')}
        options={reasonOptions}
        value={reason}
        onChange={setReason}
      />
      <Spacer size={16} />
      <Textarea
        id="decommission-note"
        label={t('device.decommission.note')}
        value={note}
        rows={3}
        maxLength={MAX_DECOMMISSION_NOTE_LENGTH}
        required={reason === DecommissionReason.OTHER}
        onValueChange={setNote}
      />
      {noteMissing && <p className="text-muted">{t('device.decommission.noteRequired')}</p>}
      <Spacer size={32} />
      <Button
        className="mb-15 error"
        isBlock
        disabled={!canSubmit}
        loading={mutation.isPending}
        buttonType="error"
        type="button"
        onClick={() => mutation.mutate()}
      >
        {t('device.decommission.confirm')}
      </Button>
      <Button onClick={() => onClose()} disabled={mutation.isPending} isBlock buttonType="link">
        {t('global.cancel')}
      </Button>
    </Modal>
  );
}
